/**
 * Magic-string lockbox for billing-module wire values + display strings.
 *
 * Every literal here is copied verbatim from Flutter
 * (`scrips_core/lib/constants/app_constants.dart`). Sources cited inline.
 * Do NOT paraphrase — backend, value-set lookups, and gate functions
 * compare directly against these strings.
 *
 * See `docs/CONVERSIONS/claim-review-popup/CONVERSION_NOTES.md` for the
 * full context and rationale.
 */

// ─── Observation types ────────────────────────────────────────────────────────
// Flutter scrips_core/constants/app_constants.dart:796-799

export const OBSERVATION_TYPE_TEXT = 'Text';
export const OBSERVATION_TYPE_RESULT = 'Result';
export const OBSERVATION_TYPE_FILE = 'File';
export const OBSERVATION_TYPE_UNIVERSAL_DENTAL = 'UniversalDental';

/** Discriminator value for `ObservationsDataModel.observationType`. */
export type ObservationType =
  | typeof OBSERVATION_TYPE_TEXT
  | typeof OBSERVATION_TYPE_RESULT
  | typeof OBSERVATION_TYPE_FILE
  | typeof OBSERVATION_TYPE_UNIVERSAL_DENTAL;

/**
 * `observationValueType` context overloads — set by the bloc per type.
 * For `Result` it holds a `dd/MM/yyyy` date string. For `Text` it's null.
 */
export const OBSERVATION_VALUE_TYPE_FILE = 'File';
export const OBSERVATION_VALUE_TYPE_TOOTH_NUMBER = 'ToothNumber';

// ─── Resubmission types (display label vs wire value) ─────────────────────────
// Flutter scrips_core/constants/app_constants.dart:773-776
//
// `'Internal Complaint'` is the DROPDOWN LABEL only. The wire value is
// `'InternalComplaint'` (no space). Flutter's `getResubmissionType()` in
// `review_claim_popup.dart:3231-3235` translates label → wire on send.

export const RESUBMISSION_TYPE_INTERNAL_COMPLAINT_LABEL = 'Internal Complaint';
export const RESUBMISSION_TYPE_INTERNAL_COMPLAINT_WIRE = 'InternalComplaint';
/** Both display and wire (no drift). */
export const RESUBMISSION_TYPE_CORRECTION = 'Correction';
/** Both display and wire (no drift). */
export const RESUBMISSION_TYPE_RECONCILIATION = 'Reconciliation';

// ─── Claim statuses ──────────────────────────────────────────────────────────
// Flutter scrips_core/constants/app_constants.dart:760-772, 779-780

export const CLAIM_STATUS_INITIAL = 'Initial';
export const CLAIM_STATUS_REVIEWED = 'Reviewed';
export const CLAIM_STATUS_REVISED_REVIEWED = 'Revised & Reviewed';
export const CLAIM_STATUS_HOLD = 'Hold';
export const CLAIM_STATUS_SUBMITTED = 'Submitted';
export const CLAIM_STATUS_REMITTED = 'Remitted';
export const CLAIM_STATUS_DENIAL_ADDRESSED = 'Denial Addressed';
export const CLAIM_STATUS_RESUBMITTED = 'Resubmitted';
export const CLAIM_STATUS_RECONCILED = 'Reconciled';
export const CLAIM_STATUS_READY_FOR_SUBMISSION = 'Ready for Submission';
export const CLAIM_STATUS_CONNECTION_ERROR = 'Connection Error';
export const CLAIM_STATUS_ERROR = 'Error';
export const CLAIM_STATUS_SENDING = 'Sending';
export const CLAIM_STATUS_PENDING = 'Pending';
export const CLAIM_STATUS_PROCESSED = 'Processed';

// ─── Invoice statuses ────────────────────────────────────────────────────────
// Flutter scrips_core/constants/app_constants.dart:732-737

export const INVOICE_STATUS_DRAFT = 'Draft';
export const INVOICE_STATUS_ISSUED = 'Issued';
export const INVOICE_STATUS_BALANCED = 'Balanced';
export const INVOICE_STATUS_CANCELLED = 'Cancelled';
export const INVOICE_STATUS_CLAIMED = 'Claimed';
export const INVOICE_STATUS_RECLAIMED = 'Reclaimed';

// ─── Coverage options ────────────────────────────────────────────────────────
// Flutter scrips_core/constants/app_constants.dart:753-755

export const COVERAGE_INCLUDED = 'Included';
export const COVERAGE_EXCLUDED = 'Excluded';
export const COVERAGE_NEEDS_APPROVAL = 'Needs approval';

// ─── Batch drill-in sentinels ────────────────────────────────────────────────
/**
 * Sentinel `claimRequestId` the backend ships for batches that have NOT yet
 * been assigned a real claim-request GUID — i.e. "Ready for Submission" batches
 * on the Submission list. The `/Submission/Details` & `/Resubmission/Details`
 * drill-ins CANNOT resolve such a batch from the GUID alone; the request body
 * MUST also carry `batchStatus[]` + `sponsors[]` (the list row's `sponsor.id`).
 *
 * This is a backend-defined all-zeros UUID, not a Flutter constant — Flutter
 * passes the row's `claimRequestId` through verbatim (no named constant), but
 * we name it here so the zero-UUID isn't an invisible literal when someone is
 * debugging a 400 on the detail endpoint. See PROD-997.
 */
export const EMPTY_CLAIM_REQUEST_ID = '00000000-0000-0000-0000-000000000000';

// ─── Multipart field name ────────────────────────────────────────────────────
// Both ClaimObservation/UploadObservationDocument and InvoiceClaim/UploadResubmissionAttachment
// use the same field name. Capital U. Verified against swagger
// (`http://localhost:6012/swagger/v1/swagger.json`).

export const MULTIPART_FIELD_UPLOADED_FILE = 'UploadedFile';

// ─── Default upload size limit ───────────────────────────────────────────────
// Flutter login_user_data_model.dart:154-156 + :200 — when JSON `maxDocumentUploadLimit`
// is missing or null, defaults to literal `10`. Both parse and serialize paths.

export const DEFAULT_MAX_DOCUMENT_UPLOAD_LIMIT_MB = 10;

/**
 * File-size check buffer multiplier (Flutter `file_picker.dart:55`).
 * Comment in Flutter: "For display purposes, we are showing 100MB but
 * while checking we are giving buffer of 5MB. So maxDocumentUploadLimit
 * is multiplied by 1.05 to give buffer of 5MB."
 */
export const FILE_SIZE_BUFFER_MULTIPLIER = 1.05;

// ─── Phase 2: observation toast + dialog strings (verbatim from Flutter) ─────

/** Flutter `add_text/result/file_observation_widget.dart` info toast on add. */
export const TOAST_OBS_ADDED = 'Observation added successfully.';
/** Flutter info toast on edit. */
export const TOAST_OBS_EDITED = 'Observation edited successfully.';
/** Flutter info toast on delete. */
export const TOAST_OBS_DELETED = 'Observation deleted successfully.';
/** Flutter `add_file_observation_widget.dart:199-200` — wrong extension toast. */
export const TOAST_FILE_INVALID =
  'Upload only PDF, RTF, JPEG, JPG, PNG, CSV, XLS, XLSX, DOC & DOCX file';
// Oversize toast is a template — interpolates maxDocumentUploadLimit at the
// call site: `File size should be less than or equal to ${limit}MB`

/** Flutter delete-confirm dialog title (`alert_dialog.dart` usage). */
export const DIALOG_DELETE_OBS_TITLE = 'Do you really want to delete this observation?';
/** Flutter delete-confirm dialog body. */
export const DIALOG_DELETE_OBS_BODY = 'If you delete, you will not able to undo this action.';
/** Flutter edit-while-other-editing confirm dialog title. */
export const DIALOG_EDIT_WHILE_OTHER_EDITING_TITLE = 'Alert';
/** Flutter edit-while-other-editing confirm dialog body. */
export const DIALOG_EDIT_WHILE_OTHER_EDITING_BODY =
  'Any unsaved changes will be lost with this action. Do you want to continue?';

/**
 * Allowed file extensions for `Add File` observation upload. Order matches
 * Flutter `add_file_observation_widget.dart:54-64` so the rejection toast
 * lists them in the same order users see in the picker.
 */
export const OBSERVATION_FILE_EXTENSIONS = [
  '.pdf',
  '.rtf',
  '.jpeg',
  '.jpg',
  '.png',
  '.csv',
  '.xls',
  '.xlsx',
  '.doc',
  '.docx',
] as const;
